import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren, AfterViewInit, OnDestroy } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NavSidebar } from '../../models/nav-sidebar';
import { NavSidebarMenuService } from '../../services/nav-sidebar-menu.service';
import { HomeUrlService } from '@modules/common/services/home-url.service';
import { NavSidebarExpandedService } from '@modules/navigation/services/nav-sidebar-expanded.service';
import { NavSidebarMenu } from '@modules/navigation/models/nav-sidebar-menu';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
selector: 'app-nav-sidebar',
templateUrl: './nav-sidebar.component.html',
styleUrls: ['./nav-sidebar.component.scss'],
animations: [
    trigger('fade', [
        state('void', style({opacity: 0})),
        transition('void <=> *', [
            animate(200)
        ])
    ])]
})
export class NavSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
    public model: NavSidebar = { menus: [] };
    public isNavSidebarExpanded: boolean = true;
    public isExpandedInitially!: boolean; //Applies an appropriate class with a fixed width to prevent its initial transition
    public isOverflowScroll!: boolean;
    public mouseOverString: string = ''; //Keeps the collapsed menu open
    private _activeURL: string = ''; //Keeps track of which item is active to scroll to it if it is ouside of the view
    private readonly _openedMenus: number[] = [];
    private readonly _menuHeight = new Map<number, string>;
    private _overflowTimer?: number;
    private _scrollTimer?: number;
    private _menuItemSubscription?: Subscription;
    private readonly _resizeObserver: ResizeObserver;

    @ViewChildren('menuItem')
    private _menuItem!: QueryList<ElementRef>;  
    @ViewChildren('activeLinkItem')
    private _activeLinkItem!: QueryList<ElementRef>;
    @ViewChild('parentElement', { static: true }) 
    private _parentElement!: ElementRef;
    @ViewChild('collapsedMenu') 
    private _collapsedMenu: ElementRef<HTMLElement> = {} as ElementRef;
    @ViewChildren('collapsedMenuActiveLink') 
    private _collapsedMenuActiveLink?: QueryList<ElementRef>;

    public homeUrl!: string;

    constructor(
        private navSidebarMenuService: NavSidebarMenuService,
        private _homeUrlService: HomeUrlService,
        private _navSidebarExpandedService: NavSidebarExpandedService,
        private _router: Router
    ) {
        _navSidebarExpandedService.toggled.subscribe(expanded => this.isNavSidebarExpanded = expanded);

        this._resizeObserver = new ResizeObserver(entries => {
            const target = entries[0].target;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;
            
            if(scrollHeight > clientHeight) {
                if(!target.classList.contains('nav-sidebar-scroll-track')){
                    target.classList.add('nav-sidebar-scroll-track');
                }
            }
            else {
                if(target.classList.contains('nav-sidebar-scroll-track')){
                    target.classList.remove('nav-sidebar-scroll-track');
                }
            }
        });
    }

    public ngOnInit(): void {
        this._homeUrlService.urlChanged.subscribe(x => this.homeUrl = x);
        this.isExpandedInitially = this.isNavSidebarExpanded;
        this.isOverflowScroll = this.isNavSidebarExpanded;
        this.updateMenus();
    }

    public ngAfterViewInit(): void {
        this.observeResize(this._parentElement.nativeElement, false);
        //scroll on refresh
        this._activeLinkItem.forEach((item: ElementRef) => {
            this.scrollToItem(item, this._parentElement);
        });
    }

    public onCollapseButtonClick(): void {
        this._navSidebarExpandedService.toggleExpanded();
        clearTimeout(this._overflowTimer);
        clearTimeout(this._scrollTimer);

        this._overflowTimer = setTimeout(() => {
            this.isOverflowScroll= !this.isOverflowScroll;
        }, 200);

        if(this.isNavSidebarExpanded) {
            this._scrollTimer = setTimeout(() => {
                this._activeLinkItem.forEach((item: ElementRef) => {
                    this.scrollToItem(item, this._parentElement);
                })
            }, 300);
        }
    }

    private updateMenus(): void {
        this._menuItemSubscription = this.navSidebarMenuService.getMenus().subscribe(x => {
            this.model = {
                menus: x,
                activeMenu: this.getActiveMenu(x)
            };

            if(this._activeLinkItem) {
                this._activeLinkItem.changes.subscribe(menuItem => {
                    menuItem.forEach((item: ElementRef) => {
                        this.scrollToItem(item, this._parentElement);
                    });
                });
            }
        });
    }

    private getActiveMenu(menus: NavSidebarMenu[]): NavSidebarMenu | undefined {
        let activeMenu: NavSidebarMenu | undefined;
        let totalMenus = 0;
        let found = false;
        let index = 0;

        for (let menu of menus) {
            if(menu.groupedMenus){
                for(let groupedMenu of menu.groupedMenus){
                    totalMenus += groupedMenu.menuItems.length + 1;
                    if(groupedMenu.menuItems && groupedMenu.menuItems.some(x => this._router.isActive(x.url, false)) && !found) {
                        found = true;
                        activeMenu = menu;
                        for(let menuItem of groupedMenu.menuItems) {
                            if(this._router.isActive(menuItem.url, false)){
                                this._activeURL = menuItem.url;
                                break;
                            }
                        }
                    }
                }
            }

            if (menu.menuItems && menu.menuItems.some(x => this._router.isActive(x.url, false))) {
                totalMenus = menu.menuItems.length;
                found = true;
                activeMenu = menu;
                for(let menuItem of menu.menuItems) {
                    if(this._router.isActive(menuItem.url, false)){
                        this._activeURL = menuItem.url;
                        break;
                    }
                }
            }

            if(found) {
                this._openedMenus.push(index);
                this._menuHeight.set(index, `${totalMenus * 50}px`);
                break;
            }
            
            index++;
            totalMenus = 0;
        }

        return activeMenu;
    }

    private scrollToItem(item: ElementRef, container: ElementRef): void {
        const pathname = decodeURIComponent(new URL(item.nativeElement.href).pathname);

        if(this._activeURL === pathname) {
            const itemDistanceFromTop = item.nativeElement.getBoundingClientRect().top;
            const containerDistanceFromTop = container.nativeElement.getBoundingClientRect().top;
            const diff = itemDistanceFromTop - containerDistanceFromTop;
            const containerHeight = container.nativeElement.clientHeight;

            if(this.isNavSidebarExpanded && itemDistanceFromTop > containerHeight) {
                container.nativeElement.scroll({
                    top: itemDistanceFromTop - containerHeight/2,
                    behavior: 'smooth'
                })
            }
            else if(!this.isNavSidebarExpanded && diff > containerHeight) {
                container.nativeElement.scroll({
                    top: diff - containerHeight/2,
                    behavior: 'smooth'
                })
            }
        }
    }

    public expand(index: number): void {
        const indexOfMenu = this._openedMenus.indexOf(index);
        const height = this._menuItem.toArray()[index].nativeElement.scrollHeight;

        if(indexOfMenu === -1) {
            this._openedMenus.push(index);
            this._menuHeight.set(index, `${height}px`);
            return;
        }

        this._openedMenus.splice(indexOfMenu, 1);
        this._menuHeight.delete(index);
    }

    public isMenuOpen(index: number): boolean {
        return this._openedMenus.includes(index);
    }

    public getHeight(index: number): string | undefined {
        return this._menuHeight.get(index);
    }

    public setMouseOver(mouseOver: string) :void {
        this.mouseOverString = mouseOver;

        this._scrollTimer = setTimeout(() => {
            this._collapsedMenuActiveLink!.forEach((item: ElementRef) => {
                this.scrollToItem(item, this._collapsedMenu);
            });
        }, 300);
    }

    public setMouseLeave(): void {
        this.mouseOverString = '';

        clearTimeout(this._scrollTimer);
        if(this._collapsedMenu) {
            this.observeResize(this._collapsedMenu.nativeElement, true);
        }
    }

    public changeScrollbar():void {
        if(this._collapsedMenu) {
            this.observeResize(this._collapsedMenu.nativeElement, false);
        }
    }

    private observeResize(element: HTMLElement, unobserve: boolean): void{
        if(unobserve) {
            this._resizeObserver.unobserve(element);
            return;
        }

        this._resizeObserver.observe(element);
    }

    public ngOnDestroy(): void {
        if(this._menuItemSubscription){
            this._menuItemSubscription.unsubscribe();
        }
        clearInterval(this._overflowTimer);
        clearTimeout(this._scrollTimer);
        this._resizeObserver.disconnect();
    }
}